import { useAuth0 } from 'app/Auth'
import { useMutation, useQuery } from 'react-query'
import { axiosInstanceWallet } from '../../../services/config/axios-config'

// Interface for surgery pass request
export interface SurgeryPassParams {
  patientId: string
  surgeryId: string
  inviteToken: string
}

/**
 * Hook to create a surgery pass
 */
export const useCreateSurgeryPass = () => {
  const { getAccessToken } = useAuth0()

  const mutation = useMutation<any, Error, SurgeryPassParams>(
    async (params: SurgeryPassParams) => {
      const token = await getAccessToken()
      const response = await axiosInstanceWallet.post(
        `/surgery_passes?inviteToken=${params.inviteToken}`,
        {
          patientId: params.patientId,
          surgeryId: params.surgeryId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      return response.data
    }
  )

  return mutation
}

/**
 * Hook to fetch surgery pass data
 */
export const useGetSurgeryPass = (
  patientId: string,
  surgeryId: string,
  inviteToken: string,
  options = {}
) => {
  const { getAccessToken } = useAuth0()

  return useQuery(
    ['surgeryPass', patientId, surgeryId, inviteToken],
    async () => {
      const token = await getAccessToken()
      const response = await axiosInstanceWallet.get(
        `/surgery_passes/${patientId}/${surgeryId}?inviteToken=${inviteToken}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      return response.data
    },
    {
      enabled: !!patientId && !!surgeryId && !!inviteToken,
      ...options,
    }
  )
}

/**
 * Get wallet pass preview URL for Apple or Google wallet
 * Note: Both Apple and Google wallets now use the Apple pass (PKPASS) format
 */
export async function downloadWalletPass(
  patientId: string,
  surgeryId: string,
  walletType: 'apple' | 'google',
  inviteToken: string,
  getAccessTokenFn: () => Promise<string>
) {
  const token = await getAccessTokenFn()
  // Both wallet types now use the apple endpoint since Android supports PKPASS
  const endpoint = 'apple'

  const response = await axiosInstanceWallet.get(
    `/surgery_passes/${patientId}/${surgeryId}/${endpoint}?inviteToken=${inviteToken}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )

  return response.data.url
}
